import { NavUser } from "@/components/common/app-sidebar/nav-user"
import { useAuth } from "@/features/auth/hooks/useAuth"
import { Badge } from "@ui/badge"
import { Button } from "@ui/button"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@ui/sidebar"
import {
  ArrowLeft,
  Cpu,
  CreditCard,
  LayoutDashboard,
  Library,
  Megaphone,
  MessageSquare,
  MessagesSquare,
  Network,
  Settings,
  Users,
  Wrench,
} from "lucide-react"
import { Link, useLocation, useNavigate } from "react-router-dom"

import { useImpersonation } from "../hooks/use-impersonation"

export function AdminSidebar() {
  const location = useLocation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { isImpersonating, impersonatedUser, stopImpersonation } = useImpersonation()

  const handleBackToSystem = () => {
    navigate("/dashboard")
  }

  const adminNavData = [
    {
      group: "Administração",
      showGroupLabel: false,
      items: [
        {
          title: "Dashboard",
          url: "/admin",
          icon: LayoutDashboard,
          get isActive() {
            return location.pathname === "/admin"
          },
        },
        {
          title: "Usuários",
          url: "/admin/users",
          icon: Users,
          get isActive() {
            return location.pathname.startsWith("/admin/users")
          },
        },
        {
          title: "Conversas",
          url: "/admin/conversations",
          icon: MessagesSquare,
          get isActive() {
            return location.pathname.startsWith("/admin/conversations")
          },
        },
        {
          title: "Prompts",
          url: "/admin/prompts",
          icon: MessageSquare,
          get isActive() {
            return location.pathname.startsWith("/admin/prompts")
          },
        },
        {
          title: "Faturamento",
          url: "/admin/billing",
          icon: CreditCard,
          get isActive() {
            return location.pathname.startsWith("/admin/billing")
          },
          badge: "Em breve",
        },
        {
          title: "Marketing",
          url: "/admin/marketing",
          icon: Megaphone,
          get isActive() {
            return location.pathname.startsWith("/admin/marketing")
          },
        },
      ],
    },
    {
      group: "Sistema",
      showGroupLabel: true,
      items: [
        {
          title: "Biblioteca",
          url: "/admin/library",
          icon: Library,
          get isActive() {
            return location.pathname.startsWith("/admin/library")
          },
        },
        {
          title: "Modelos LLM",
          url: "/admin/llm-models",
          icon: Cpu,
          get isActive() {
            return location.pathname.startsWith("/admin/llm-models")
          },
        },
        {
          title: "Ferramentas",
          url: "/admin/tools",
          icon: Wrench,
          get isActive() {
            return location.pathname.startsWith("/admin/tools")
          },
        },
        {
          title: "Tech Providers",
          url: "/admin/tech-providers",
          icon: Network,
          get isActive() {
            return location.pathname.startsWith("/admin/tech-providers")
          },
        },
        {
          title: "Configurações",
          url: "/admin/settings",
          icon: Settings,
          get isActive() {
            return location.pathname.startsWith("/admin/settings")
          },
        },
      ],
    },
  ]

  const data = {
    user: {
      name: user?.name || "",
      email: user?.email || "",
      avatar: "/avatars/shadcn.jpg",
    },
    nav: adminNavData,
  }

  return (
    <Sidebar variant="inset">
      <SidebarHeader className="flex justify-start px-6 py-3">
        <div className="flex items-center gap-3">
          <img src="/front-end/speeke2.svg" alt="Speeke" className="h-8 w-30" />
          <Badge variant="secondary" className="text-xs">
            Admin
          </Badge>
        </div>
      </SidebarHeader>

      <SidebarContent>
        {/* Botão Voltar para o Sistema */}
        <SidebarGroup>
          <SidebarGroupContent>
            <div className="px-2 pb-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleBackToSystem}
                className="w-full justify-start gap-2 text-sm"
              >
                <ArrowLeft className="h-4 w-4" />
                Voltar para o Sistema
              </Button>
            </div>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Menu de Navegação Admin */}
        {data.nav.map(navGroup => (
          <SidebarGroup key={navGroup.group}>
            {navGroup.showGroupLabel && <SidebarGroupLabel>{navGroup.group}</SidebarGroupLabel>}
            <SidebarGroupContent>
              <SidebarMenu>
                {navGroup.items?.map(item => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild isActive={item.isActive}>
                      <Link to={item.url || "#"} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <item.icon className="h-4 w-4" />
                          <span>{item.title}</span>
                        </div>
                        {item.badge && (
                          <Badge variant="secondary" className="ml-auto text-xs">
                            {item.badge}
                          </Badge>
                        )}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter>
        {/* Impersonation indicator */}
        {isImpersonating && impersonatedUser && (
          <div className="px-2 py-2">
            <div className="flex flex-col gap-2 rounded-md border border-amber-500/50 bg-amber-50 p-2 dark:bg-amber-950/30">
              <div className="flex-1 text-sm">
                <div className="font-medium text-amber-800 dark:text-amber-300">Impersonando</div>
                <div className="text-xs text-amber-700 dark:text-amber-400">
                  {impersonatedUser.name}
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={stopImpersonation}
                className="w-full gap-1.5 border-amber-500 text-amber-800 hover:bg-amber-100 dark:text-amber-300 dark:hover:bg-amber-900/50"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Voltar para Admin
              </Button>
            </div>
          </div>
        )}

        {/* Informação do Usuário Admin */}
        {!isImpersonating && (
          <div className="px-2 py-2">
            <div className="bg-muted/50 flex items-center gap-2 rounded-md p-2">
              <div className="flex-1 text-sm">
                <div className="text-muted-foreground font-medium">Modo Admin</div>
                <div className="text-muted-foreground text-xs">Acesso administrativo ativo</div>
              </div>
              <Badge variant="default" className="text-xs">
                Admin
              </Badge>
            </div>
          </div>
        )}

        <NavUser user={data.user} />
      </SidebarFooter>
    </Sidebar>
  )
}
